import { useContext, useEffect, useState } from "react";
import {
  QuestionnaireClient,
  QuestionnaireResponseDataViewModel,
  QuestionnaireResponseViewModel,
  QuestionnaireViewModel,
  ResponseState,
} from "../../types/auto/types";
import { Configuration } from "../Constants";
import { FetchOverride } from "../utils/Request";
import { useNavigate, useSearchParams } from "react-router-dom";
import QuestionnaireForm from "../components/questionnaires/QuestionnaireForm";
import { Error500 } from "../components/elements/errors/Error500";
import { Alert, CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useMountedState, useUnmountPromise } from "react-use";
import { UserContext } from "../components/elements/stores/UserStore";
import PAXDTool from "../components/questionnaires/PaxDTool";

const Questionnaire = () => {
  let navigate = useNavigate();
  const user = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [error500, setError500] = useState(false);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);
  const [tokens, setTokens] = useState(searchParams.get("t")?.split(",") || []);
  const patientId = searchParams.get("p");
  const editing = searchParams.get("e") ? true : false;
  const fromNotification = searchParams.get("fn") ? true : false;
  const tenantId = searchParams.get("tId");
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireViewModel>();
  const [questionnaireResponseData, setQuestionnaireResponseData] =
    useState<QuestionnaireResponseDataViewModel>();
  const [rerender, setRerender] = useState(false);
  const [loadQuestionnaire, setloadQuestionnaire] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [openSaveDraftModal, setOpenSaveDraftModal] = useState(false);
  const [remainingTokens, setRemainingTokens] = useState(tokens.length);

  const [draftNotFoundError, setDraftNotFoundError] = useState(false);

  const useStyle = makeStyles({
    loadingSpinner: {
      margin: "auto",
      width: "100%",
      marginLeft: "46%",
    },
  });
  const classes = useStyle();

  const resolveWhileMounted = useUnmountPromise();
  const isMountedState = useMountedState();

  useEffect(() => {
    const effect = async () => {
      if (isMountedState()) {
        if (tokens.length > 0) {
          setloadQuestionnaire(true);
          await resolveWhileMounted(
            new QuestionnaireClient(
              Configuration.SERVER_ROOT,
              FetchOverride
            ).getQuestionnaireViaToken(tokens[0], editing)
          )
            .then(async (questionnaire: QuestionnaireViewModel) => {
              if (questionnaire.id !== undefined) {
                setQuestionnaire(questionnaire);
                await resolveWhileMounted(
                  new QuestionnaireClient(
                    Configuration.SERVER_ROOT,
                    FetchOverride
                  ).getQuestionnaireResponses(tokens[0])
                )
                  .then((responses) => {
                    setQuestionnaireResponseData(responses);
                    setloadQuestionnaire(false);
                  })
                  .catch(() => setloadQuestionnaire(false));
              } else {
                var array = tokens;
                array?.shift();
                setTokens(array);
                setRemainingTokens(array.length);
                setRerender((x) => !x);
                setloadQuestionnaire(false);
              }
            })
            .catch((e) => {
              if (e.status === 400) {
                var array = tokens;
                array?.shift();
                setTokens(array);
                setRemainingTokens(array.length);
                setRerender((x) => !x);
                setloadQuestionnaire(false);
              } else {
                setError500(true);
                setloadQuestionnaire(false);
              }
            });
        } else {
          setloadQuestionnaire(false);
          setOpenSubmitDialog(true);
        }
      }
    };
    effect();
  }, [
    navigate,
    rerender,
    tokens,
    patientId,
    editing,
    resolveWhileMounted,
    isMountedState,
  ]);

  async function handleDraftDiscard() {
    await new QuestionnaireClient(Configuration.SERVER_ROOT, FetchOverride)
      .discardDraftResponses(tokens[0])
      .then(() => {
        setRerender((x) => !x);
      })
      .catch(() => {
        setDraftNotFoundError(true);
      });
  }

  return (
    <>
      {loadQuestionnaire ? (
        <CircularProgress
          className={classes.loadingSpinner}
          aria-label="Loading"
        />
      ) : questionnaire?.paxTaskNumber !== undefined &&
        questionnaire?.paxTaskNumber !== null ? (
        <PAXDTool questionnaire={questionnaire} patientId={patientId} />
      ) : (
        <QuestionnaireForm
          patientId={patientId}
          questionnaire={questionnaire}
          openSubmitDialog={openSubmitDialog}
          submitResponses={(
            responses: Array<QuestionnaireResponseViewModel>,
            date: Date,
            state: ResponseState
          ) =>
            new QuestionnaireClient(
              Configuration.SERVER_ROOT,
              FetchOverride
            ).submitQuestionnaireResponse(
              tokens[0],
              responses,
              editing,
              editingDate,
              state,
              date
            )
          }
          getNextQuestionnaire={(draft) => {
            if (tokens.length > 1) {
              setOpenSubmitDialog(false);
              setOpenSaveDraftModal(false);
              var array = tokens;
              array?.shift();
              setTokens(array);
              setRemainingTokens(array.length);
              setRerender((x) => !x);
              setQuestionnaireResponseData(
                new QuestionnaireResponseDataViewModel()
              );
              window.scrollTo({ top: 0 });
            } else {
              if (draft) {
                setOpenSaveDraftModal(true);
              } else {
                setOpenSubmitDialog(true);
              }
            }
          }}
          questionnaireResponseData={questionnaireResponseData}
          editing={editing}
          editingDate={editingDate}
          setEditingDate={setEditingDate}
          goal={false}
          role={user.role}
          fromNotification={fromNotification}
          submitDraft={(
            responses: Array<QuestionnaireResponseViewModel>,
            date: Date,
            state: ResponseState
          ) =>
            new QuestionnaireClient(
              Configuration.SERVER_ROOT,
              FetchOverride
            ).submitQuestionnaireResponse(
              tokens[0],
              responses,
              editing,
              editingDate,
              state,
              date
            )
          }
          responseToken={tokens[0]}
          tenantId={tenantId}
          handleDiscard={() => handleDraftDiscard()}
          editDraftError={draftNotFoundError}
          openSaveDraftModal={openSaveDraftModal}
          remainingTokens={remainingTokens}
          setOpenSaveDraftModal={setOpenSaveDraftModal}
        />
      )}
      {draftNotFoundError && <Alert severity="error">Draft not found</Alert>}
      {error500 && <Error500 />}
    </>
  );
};

export default Questionnaire;
